/**
 * Local SEO / AEO / GEO helpers — no external AI APIs.
 * Heuristics from title, HTML body, focus keyword, and locations.
 */

export type FaqItem = { q: string; a: string }
export type HowToStep = { name: string; text: string }

export type ContentStats = {
  plainText: string
  wordCount: number
  charCount: number
  readingMinutes: number
  h2s: string[]
  h3s: string[]
  paragraphs: string[]
  first100Words: string
  keywordCount: number
  keywordDensity: number
  keywordInTitle: boolean
  keywordInFirst100: boolean
  keywordInH2: boolean
  hasLists: boolean
  hasImages: boolean
  hasInternalLinks: boolean
  hasExternalLinks: boolean
}

const STOP = new Set(
  'a an the and or but in on at to for of is are was were be been being this that with from as by it its into over under about after before between'.split(
    ' '
  )
)

export function stripHtml(html: string): string {
  if (!html) return ''
  if (typeof document !== 'undefined') {
    const el = document.createElement('div')
    el.innerHTML = html
    return (el.textContent || el.innerText || '').replace(/\s+/g, ' ').trim()
  }
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function extractTags(html: string, tag: string): string[] {
  const re = new RegExp(`<${tag}[^>]*>([\\s\\S]*?)<\\/${tag}>`, 'gi')
  const out: string[] = []
  let m: RegExpExecArray | null
  while ((m = re.exec(html))) {
    const t = stripHtml(m[1]).trim()
    if (t) out.push(t)
  }
  return out
}

function extractParagraphs(html: string): string[] {
  const fromP = extractTags(html, 'p')
  if (fromP.length) return fromP
  const plain = stripHtml(html)
  return plain
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 40)
}

function countKeyword(text: string, keyword: string): number {
  const k = keyword.trim().toLowerCase()
  if (!k) return 0
  const hay = text.toLowerCase()
  if (k.includes(' ')) {
    let n = 0
    let i = 0
    while ((i = hay.indexOf(k, i)) !== -1) {
      n++
      i += k.length
    }
    return n
  }
  const words = hay.split(/[^a-z0-9+#]+/).filter(Boolean)
  return words.filter((w) => w === k).length
}

export function analyzeContent(html: string, title: string, focusKeyword: string): ContentStats {
  const plainText = stripHtml(html)
  const words = plainText ? plainText.split(/\s+/).filter(Boolean) : []
  const wordCount = words.length
  const h2s = extractTags(html, 'h2')
  const h3s = extractTags(html, 'h3')
  const paragraphs = extractParagraphs(html)
  const first100Words = words.slice(0, 100).join(' ')
  const keywordCount = countKeyword(plainText, focusKeyword)
  const keywordDensity = wordCount ? (keywordCount / wordCount) * 100 : 0
  const kw = focusKeyword.trim().toLowerCase()

  return {
    plainText,
    wordCount,
    charCount: plainText.length,
    readingMinutes: Math.max(1, Math.round(wordCount / 220) || 1),
    h2s,
    h3s,
    paragraphs,
    first100Words,
    keywordCount,
    keywordDensity,
    keywordInTitle: kw ? title.toLowerCase().includes(kw) : false,
    keywordInFirst100: kw ? first100Words.toLowerCase().includes(kw) : false,
    keywordInH2: kw ? h2s.some((h) => h.toLowerCase().includes(kw)) : false,
    hasLists: /<(ul|ol)\b/i.test(html),
    hasImages: /<img\b/i.test(html),
    hasInternalLinks: /href=["']\/[^"']+/i.test(html),
    hasExternalLinks: /href=["']https?:\/\//i.test(html),
  }
}

/** Latest practical SERP / AEO / GEO limits (2024–2026). */
export const SEO_LIMITS = {
  titleMax: 60,
  titleIdeal: [50, 60] as [number, number],
  metaMax: 160,
  metaIdeal: [140, 160] as [number, number],
  aiSummaryMax: 300,
  aiSummaryIdeal: [120, 220] as [number, number],
  ogTitleMax: 60,
  ogDescMax: 160,
  keywordDensityIdeal: [0.5, 2.5] as [number, number],
  minWords: 800,
  idealFaq: [3, 6] as [number, number],
  idealTakeaways: [3, 5] as [number, number],
  idealHowTo: [3, 8] as [number, number],
  idealRelatedQueries: [3, 8] as [number, number],
}

export const SEARCH_INTENTS = [
  { value: 'informational', label: 'Informational (learn / how-to)' },
  { value: 'commercial', label: 'Commercial (compare / best)' },
  { value: 'transactional', label: 'Transactional (buy / book)' },
  { value: 'navigational', label: 'Navigational (brand)' },
] as const

export const SCHEMA_TYPES = [
  { value: 'Article', label: 'Article' },
  { value: 'BlogPosting', label: 'BlogPosting' },
  { value: 'CaseStudy', label: 'CaseStudy' },
  { value: 'HowTo', label: 'HowTo' },
  { value: 'NewsArticle', label: 'NewsArticle' },
] as const

function clip(s: string, max: number) {
  const t = s.trim().replace(/\s+/g, ' ')
  if (t.length <= max) return t
  const cut = t.slice(0, max - 1)
  const lastSpace = cut.lastIndexOf(' ')
  return (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd() + '…'
}

export function suggestSeoTitle(title: string, focusKeyword: string, locations?: string): string {
  const kw = focusKeyword.trim()
  const loc = (locations || '').split(',')[0]?.trim()
  let base = title.trim() || kw || 'Untitled insight'
  if (kw && !base.toLowerCase().includes(kw.toLowerCase())) {
    base = `${kw}: ${base}`
  }
  if (loc && base.length < 45 && !base.toLowerCase().includes(loc.toLowerCase())) {
    base = `${base} | ${loc}`
  }
  if (!base.toLowerCase().includes('digisutra') && base.length < 48) {
    base = `${base} | DigiSutra`
  }
  return clip(base, SEO_LIMITS.titleMax).replace(/…$/, '')
}

export function suggestMetaDescription(
  summary: string,
  html: string,
  focusKeyword: string,
  locations?: string
): string {
  const stats = analyzeContent(html, '', focusKeyword)
  let lead =
    summary.trim() ||
    stats.paragraphs[0] ||
    stats.plainText.slice(0, 200) ||
    'Practical digital marketing insights from DigiSutra.'
  const kw = focusKeyword.trim()
  if (kw && !lead.toLowerCase().includes(kw.toLowerCase())) {
    lead = `${kw} — ${lead}`
  }
  const loc = (locations || '').split(',')[0]?.trim()
  if (loc && lead.length < 120) {
    lead = `${lead} Serving ${loc} and nearby markets.`
  }
  if (lead.length < 120) {
    lead = `${lead} Learn frameworks, tactics, and takeaways you can apply this week.`
  }
  return clip(lead, SEO_LIMITS.metaMax).replace(/…$/, '')
}

export function suggestKeywords(
  focusKeyword: string,
  tags: string,
  locations: string,
  title: string,
  html: string
): string {
  const bag = new Map<string, number>()
  const add = (raw: string, weight = 1) => {
    const t = raw.trim().toLowerCase()
    if (!t || t.length < 3 || STOP.has(t)) return
    bag.set(t, (bag.get(t) || 0) + weight)
  }

  focusKeyword
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((k) => add(k, 5))
  tags
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((k) => add(k, 3))
  locations
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((k) => add(k, 2))

  title
    .toLowerCase()
    .split(/[^a-z0-9+#]+/)
    .filter((w) => w.length > 3)
    .forEach((w) => add(w, 2))

  const stats = analyzeContent(html, title, focusKeyword)
  stats.h2s.forEach((h) =>
    h
      .toLowerCase()
      .split(/[^a-z0-9+#]+/)
      .filter((w) => w.length > 4)
      .forEach((w) => add(w, 1))
  )

  return [...bag.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 12)
    .map(([k]) => k)
    .join(', ')
}

export function suggestTakeaways(html: string, title: string, focusKeyword: string): string {
  const stats = analyzeContent(html, title, focusKeyword)
  const lines: string[] = []

  for (const h of stats.h2s) {
    if (lines.length >= 5) break
    if (h.length > 12 && h.length < 140) lines.push(h.replace(/[:.]+$/, ''))
  }

  for (const p of stats.paragraphs) {
    if (lines.length >= 5) break
    const sentence = p.split(/(?<=[.!?])\s+/)[0]?.trim()
    if (sentence && sentence.length > 40 && sentence.length < 160) {
      if (!lines.some((l) => l.slice(0, 30) === sentence.slice(0, 30))) {
        lines.push(sentence.replace(/[.!?]+$/, ''))
      }
    }
  }

  if (lines.length < 3) {
    const kw = focusKeyword.trim() || title || 'this topic'
    lines.push(
      `Clarify the goal before spending on ${kw}`,
      `Measure leading indicators weekly, not only vanity metrics`,
      `Systemize what works so results compound`
    )
  }

  return lines.slice(0, 5).join('\n')
}

export function suggestAiSummary(
  summary: string,
  html: string,
  focusKeyword: string,
  locations?: string
): string {
  const stats = analyzeContent(html, '', focusKeyword)
  const kw = focusKeyword.trim()
  const loc = (locations || '').split(',')[0]?.trim()
  let lead =
    summary.trim() ||
    stats.paragraphs[0] ||
    stats.plainText.slice(0, 180) ||
    'A practical DigiSutra guide with clear steps and takeaways.'
  if (kw && !lead.toLowerCase().includes(kw.toLowerCase())) {
    lead = `${kw}: ${lead}`
  }
  if (loc && lead.length < 160) {
    lead = `${lead} Relevant for teams in ${loc}.`
  }
  return clip(lead, SEO_LIMITS.aiSummaryMax).replace(/…$/, '')
}

export function suggestRelatedQueries(
  title: string,
  focusKeyword: string,
  locations?: string,
  html?: string
): string {
  const kw = focusKeyword.trim() || title.trim() || 'digital marketing'
  const loc = (locations || '').split(',')[0]?.trim() || 'Surat'
  const stats = html ? analyzeContent(html, title, focusKeyword) : null
  const lines = [
    `What is ${kw}?`,
    `How to improve ${kw}`,
    `Best ${kw} for small business`,
    `${kw} in ${loc}`,
    `Common mistakes in ${kw}`,
  ]
  if (stats?.h2s.length) {
    for (const h of stats.h2s.slice(0, 3)) {
      lines.push(h.replace(/\?*$/, '').length < 80 ? `${h.replace(/\?*$/, '')}?` : h)
    }
  }
  return [...new Set(lines)].slice(0, 6).join('\n')
}

export function suggestHowToSteps(html: string, title: string, focusKeyword: string): HowToStep[] {
  const stats = analyzeContent(html, title, focusKeyword)
  const steps: HowToStep[] = []

  for (const h of stats.h2s) {
    if (steps.length >= 6) break
    if (/^(faq|conclusion|summary|related)/i.test(h)) continue
    const idx = stats.h2s.indexOf(h)
    const body =
      stats.paragraphs[Math.min(idx, stats.paragraphs.length - 1)] ||
      `Follow this step carefully for ${focusKeyword || title || 'better results'}.`
    steps.push({
      name: clip(h, 100).replace(/…$/, ''),
      text: clip(body, 280).replace(/…$/, ''),
    })
  }

  if (steps.length < 3) {
    const kw = focusKeyword.trim() || title || 'your goal'
    const defaults: HowToStep[] = [
      { name: 'Define the outcome', text: `Write a clear success metric for ${kw} before spending time or budget.` },
      { name: 'Set up tracking', text: 'Confirm conversions, UTMs, and CRM fields so you can measure what works.' },
      { name: 'Ship a first campaign or page', text: 'Launch a focused MVP, review weekly, then scale what converts.' },
      { name: 'Iterate with evidence', text: 'Double down on winning messages and cut weak variants every sprint.' },
    ]
    for (const d of defaults) {
      if (steps.length >= 4) break
      steps.push(d)
    }
  }
  return steps.slice(0, 6)
}

export function suggestSecondaryKeywords(
  focusKeyword: string,
  tags: string,
  locations: string,
  title: string,
  html: string
): string {
  const primary = focusKeyword.trim().toLowerCase()
  const all = suggestKeywords(focusKeyword, tags, locations, title, html)
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s && s.toLowerCase() !== primary)
  return all.slice(0, 8).join(', ')
}

export function suggestFaqs(
  html: string,
  title: string,
  focusKeyword: string,
  locations?: string
): FaqItem[] {
  const stats = analyzeContent(html, title, focusKeyword)
  const kw = focusKeyword.trim() || title || 'digital marketing'
  const loc = (locations || '').split(',')[0]?.trim() || 'Surat'
  const faqs: FaqItem[] = []

  const answerFromHeading = (heading: string): string => {
    const idx = stats.h2s.indexOf(heading)
    const next = stats.paragraphs[Math.min(idx + 1, stats.paragraphs.length - 1)]
    const body =
      next ||
      stats.paragraphs[0] ||
      `${heading} is covered in this DigiSutra insight with practical steps you can apply immediately.`
    return clip(body, 320).replace(/…$/, '')
  }

  for (const h of stats.h2s) {
    if (faqs.length >= 5) break
    const isQ = /\?$/.test(h) || /^(what|why|how|when|where|who|which|can|should|is|are|do|does)\b/i.test(h)
    const q = isQ ? h.replace(/\?*$/, '?') : `What should I know about ${h}?`
    faqs.push({ q: clip(q, 120).replace(/…$/, ''), a: answerFromHeading(h) })
  }

  const defaults: FaqItem[] = [
    {
      q: `What is ${kw}?`,
      a:
        stats.paragraphs[0] ||
        `${kw} is explained in this guide with clear definitions and actionable next steps from DigiSutra.`,
    },
    {
      q: `How can businesses in ${loc} use ${kw}?`,
      a: `Businesses in ${loc} can apply the frameworks in this article to generate qualified demand, improve messaging, and measure ROI week over week.`,
    },
    {
      q: `How long does it take to see results from ${kw}?`,
      a: `Most teams see early signal within 2–4 weeks if tracking is clean; compounding results usually take a full quarter of consistent execution.`,
    },
    {
      q: `Do I need an agency for ${kw}?`,
      a: `You can start in-house with the playbook here. Partner with DigiSutra when you need speed, media buying depth, or a full growth system.`,
    },
  ]

  for (const d of defaults) {
    if (faqs.length >= 5) break
    if (!faqs.some((f) => f.q.toLowerCase() === d.q.toLowerCase())) {
      faqs.push({ q: d.q, a: clip(d.a, 320).replace(/…$/, '') })
    }
  }

  return faqs.slice(0, 5)
}

export type SeoCheck = { id: string; ok: boolean; label: string; tip?: string }

export function buildSeoChecks(opts: {
  title: string
  summary: string
  html: string
  seoTitle: string
  seoDescription: string
  focusKeyword: string
  secondaryKeywords: string
  targetLocations: string
  primaryCity: string
  serviceArea: string
  keyTakeaways: string
  faq: FaqItem[]
  howTo: HowToStep[]
  relatedQueries: string
  aiSummary: string
  authorName: string
  authorRole: string
  searchIntent: string
  schemaType: string
  ogTitle: string
  reviewedAt: string
}): SeoCheck[] {
  const {
    html,
    title,
    seoTitle,
    seoDescription,
    focusKeyword,
    secondaryKeywords,
    targetLocations,
    primaryCity,
    serviceArea,
    keyTakeaways,
    faq,
    howTo,
    relatedQueries,
    aiSummary,
    authorName,
    authorRole,
    searchIntent,
    schemaType,
    ogTitle,
    reviewedAt,
    summary,
  } = opts
  const stats = analyzeContent(html, title, focusKeyword)
  const takeawayCount = keyTakeaways
    .split(/\n+/)
    .map((s) => s.trim())
    .filter(Boolean).length
  const relatedCount = relatedQueries
    .split(/\n+/)
    .map((s) => s.trim())
    .filter(Boolean).length
  const densityOk =
    !focusKeyword.trim() ||
    (stats.keywordDensity >= SEO_LIMITS.keywordDensityIdeal[0] &&
      stats.keywordDensity <= SEO_LIMITS.keywordDensityIdeal[1])
  const howToOk =
    schemaType !== 'HowTo' || howTo.filter((s) => s.name && s.text).length >= SEO_LIMITS.idealHowTo[0]

  return [
    {
      id: 'words',
      ok: stats.wordCount >= SEO_LIMITS.minWords,
      label: `Body ≥ ${SEO_LIMITS.minWords} words (${stats.wordCount})`,
      tip: 'Long-form posts rank and get cited more often by answer engines.',
    },
    { id: 'summary', ok: summary.trim().length >= 80, label: 'Summary ≥ 80 chars' },
    { id: 'focus', ok: !!focusKeyword.trim(), label: 'Focus keyword set' },
    {
      id: 'secondary',
      ok: secondaryKeywords.split(',').filter((s) => s.trim()).length >= 2,
      label: 'Secondary keywords ≥ 2',
    },
    {
      id: 'kw-title',
      ok:
        stats.keywordInTitle ||
        (!!focusKeyword && seoTitle.toLowerCase().includes(focusKeyword.toLowerCase())),
      label: 'Keyword in title / SEO title',
    },
    {
      id: 'kw-intro',
      ok: !focusKeyword.trim() || stats.keywordInFirst100,
      label: 'Keyword in first 100 words',
    },
    {
      id: 'kw-h2',
      ok: !focusKeyword.trim() || stats.keywordInH2 || stats.h2s.length === 0,
      label: 'Keyword in an H2 (or add H2s)',
    },
    {
      id: 'density',
      ok: densityOk,
      label: `Keyword density ${stats.keywordDensity.toFixed(1)}% (ideal 0.5–2.5%)`,
    },
    {
      id: 'seo-title',
      ok: seoTitle.length >= SEO_LIMITS.titleIdeal[0] && seoTitle.length <= SEO_LIMITS.titleIdeal[1],
      label: `SEO title ${SEO_LIMITS.titleIdeal[0]}–${SEO_LIMITS.titleIdeal[1]} chars`,
    },
    {
      id: 'meta',
      ok:
        seoDescription.length >= SEO_LIMITS.metaIdeal[0] &&
        seoDescription.length <= SEO_LIMITS.metaIdeal[1],
      label: `Meta ${SEO_LIMITS.metaIdeal[0]}–${SEO_LIMITS.metaIdeal[1]} chars`,
    },
    {
      id: 'ai-summary',
      ok:
        aiSummary.length >= SEO_LIMITS.aiSummaryIdeal[0] &&
        aiSummary.length <= SEO_LIMITS.aiSummaryMax,
      label: `AI / answer summary ${SEO_LIMITS.aiSummaryIdeal[0]}–${SEO_LIMITS.aiSummaryMax}`,
      tip: 'Short direct answer for AEO & generative engines.',
    },
    { id: 'h2', ok: stats.h2s.length >= 2, label: `At least 2 H2 sections (${stats.h2s.length})` },
    { id: 'lists', ok: stats.hasLists, label: 'Includes a list (scannable / AEO)' },
    { id: 'images', ok: stats.hasImages, label: 'At least 1 image in body' },
    {
      id: 'takeaways',
      ok: takeawayCount >= SEO_LIMITS.idealTakeaways[0],
      label: `Key takeaways ≥ ${SEO_LIMITS.idealTakeaways[0]}`,
    },
    {
      id: 'faq',
      ok: faq.filter((f) => f.q && f.a).length >= SEO_LIMITS.idealFaq[0],
      label: `FAQ ≥ ${SEO_LIMITS.idealFaq[0]} (schema / AEO)`,
    },
    {
      id: 'howto',
      ok: howToOk,
      label:
        schemaType === 'HowTo'
          ? `HowTo steps ≥ ${SEO_LIMITS.idealHowTo[0]}`
          : `How-to steps optional (${howTo.filter((s) => s.name && s.text).length})`,
    },
    {
      id: 'related',
      ok: relatedCount >= SEO_LIMITS.idealRelatedQueries[0],
      label: `Related queries ≥ ${SEO_LIMITS.idealRelatedQueries[0]} (GEO/AEO)`,
    },
    { id: 'intent', ok: !!searchIntent, label: 'Search intent set' },
    { id: 'schema', ok: !!schemaType, label: 'Schema type set' },
    { id: 'author', ok: !!authorName.trim(), label: 'Author name (E-E-A-T)' },
    { id: 'author-role', ok: !!authorRole.trim(), label: 'Author role (E-E-A-T)' },
    { id: 'city', ok: !!primaryCity.trim(), label: 'Primary city (GEO)' },
    {
      id: 'geo',
      ok: targetLocations.trim().length > 0 || serviceArea.trim().length > 0,
      label: 'Locations or service area (GEO)',
    },
    {
      id: 'og',
      ok: !!ogTitle.trim() || seoTitle.length > 0,
      label: 'OG / social title ready',
    },
    {
      id: 'reviewed',
      ok: !!reviewedAt,
      label: 'Reviewed date (freshness)',
    },
    {
      id: 'internal',
      ok: stats.hasInternalLinks,
      label: 'Internal link present',
      tip: 'Link to /services, /contact, or related insights.',
    },
  ]
}
